/**
 * BoardMembersTab - Board members grid view for AdminStaff page
 */
import { Button } from "../ui/button";
import { 
  UserPlus, Crown, Shield, Edit, Trash2, Send, 
  User, Briefcase, Award, Building, GraduationCap, Star, Linkedin
} from "lucide-react";
import { BOARD_POSITIONS } from "./constants";

// Position icons mapping
const POSITION_ICONS = {
  president: Crown,
  vice_president: Star,
  secretary: Briefcase,
  treasurer: Building,
  director: Award,
  member_at_large: User,
  advisor: GraduationCap,
  other: User
};

const BoardMembersTab = ({
  boardMembers,
  searchQuery,
  onAddMember,
  onEditMember,
  onDeleteMember,
  onSendInvite
}) => {
  // Filter board members
  const filteredBoard = boardMembers.filter(member => {
    const matchesSearch = !searchQuery || 
      `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      {filteredBoard.length === 0 ? (
        <div className="text-center py-12">
          <Crown className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-1">No board members</h3>
          <p className="text-slate-500 mb-4">Add members of your board of directors</p>
          <Button onClick={onAddMember} className="bg-slate-900 rounded-full" data-testid="add-board-member-empty">
            <UserPlus className="w-4 h-4 mr-2" /> Add Board Member
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
          {filteredBoard.map((member) => {
            const PositionIcon = POSITION_ICONS[member.position] || User;
            return (
              <div 
                key={member.member_id} 
                className="bg-slate-50 rounded-xl p-4 relative group"
                data-testid={`board-member-card-${member.member_id}`}
              >
                {/* Hover actions */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onEditMember(member)} 
                    className="h-8 w-8 p-0 rounded-lg"
                    data-testid={`edit-board-${member.member_id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => onDeleteMember(member.member_id)} 
                    className="h-8 w-8 p-0 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-board-${member.member_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Member info */}
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
                    <PositionIcon className="w-6 h-6 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{member.first_name} {member.last_name}</p>
                    <p className="text-sm text-slate-600">
                      {member.title || BOARD_POSITIONS.find(p => p.value === member.position)?.label}
                    </p>
                    {member.email && <p className="text-xs text-slate-500 truncate mt-1">{member.email}</p>}
                    {member.term_start && (
                      <p className="text-xs text-slate-400 mt-1">
                        Term: {member.term_start} - {member.term_end || "Present"}
                      </p>
                    )}
                    {member.has_system_access && (
                      <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">
                        <Shield className="w-3 h-3" />
                        System Access
                      </span>
                    )}
                  </div>
                </div>

                {/* Bio */}
                {member.bio && (
                  <p className="text-sm text-slate-600 mt-3 line-clamp-2">{member.bio}</p>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 mt-2">
                  {member.linkedin_url && (
                    <a 
                      href={member.linkedin_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                    >
                      <Linkedin className="w-3 h-3" /> LinkedIn
                    </a>
                  )}
                  {member.has_system_access && !member.user_id && member.email && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-xs text-purple-600 h-6 px-2"
                      onClick={() => onSendInvite(member)}
                      data-testid={`invite-board-${member.member_id}`}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {member.invite_sent ? "Resend Invite" : "Send Invite"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BoardMembersTab;
